import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import IntroFieldsPanel from '../components/IntroFieldsPanel';
import CopyCandidatesModal from '../components/CopyCandidatesModal';
import { END_REASON_LABELS } from '../utils/endReason';

const SessionDetail = () => {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showValidityModal, setShowValidityModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [candidates, setCandidates] = useState([{ name: '', email: '', valid_from: '', valid_until: '' }]);
  const [addingCandidates, setAddingCandidates] = useState(false);
  const [useCustomValidity, setUseCustomValidity] = useState(false);
  const [exporting, setExporting] = useState(null); // 'links' | 'results' | null
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [sendingEmail, setSendingEmail] = useState({});
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [selectedForEmail, setSelectedForEmail] = useState([]);

  // Check if email is configured
  useEffect(() => {
    const checkEmailStatus = async () => {
      try {
        const response = await api.get('/sessions/email/status');
        setEmailConfigured(response.data.configured);
      } catch (error) {
        console.log('Email not configured');
      }
    };
    checkEmailStatus();
  }, []);

  // Convert datetime-local string to ISO string preserving local time
  const toISOWithTimezone = (datetimeLocalStr) => {
    if (!datetimeLocalStr) return null;
    const date = new Date(datetimeLocalStr);
    return date.toISOString();
  };

  // Convert ISO/UTC date string to datetime-local format
  const toDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    fetchSession();
    api.get('/sessions').then(r => setAllSessions(r.data)).catch(() => {});
  }, [id]);

  const fetchSession = async () => {
    try {
      const response = await api.get(`/sessions/${id}`);
      setSession(response.data);
    } catch (error) {
      setError('Failed to load session');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const addCandidateRow = () => {
    setCandidates([...candidates, { name: '', email: '', valid_from: '', valid_until: '' }]);
  };

  const removeCandidateRow = (index) => {
    if (candidates.length > 1) {
      setCandidates(candidates.filter((_, i) => i !== index));
    }
  };

  const updateCandidate = (index, field, value) => {
    const updated = [...candidates];
    updated[index][field] = value;
    setCandidates(updated);
  };

  const handleAddCandidates = async (e) => {
    e.preventDefault();
    setAddingCandidates(true);
    setError('');

    // Filter out empty rows and prepare data
    const validCandidates = candidates
      .filter(c => c.name.trim() && c.email.trim())
      .map(c => ({
        name: c.name,
        email: c.email,
        valid_from: useCustomValidity && c.valid_from ? toISOWithTimezone(c.valid_from) : null,
        valid_until: useCustomValidity && c.valid_until ? toISOWithTimezone(c.valid_until) : null
      }));

    if (validCandidates.length === 0) {
      setError('Please add at least one candidate with name and email');
      setAddingCandidates(false);
      return;
    }

    try {
      await api.post(`/sessions/${id}/invites`, { candidates: validCandidates });
      setShowAddModal(false);
      setCandidates([{ name: '', email: '', valid_from: '', valid_until: '' }]);
      setUseCustomValidity(false);
      fetchSession();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add candidates');
    } finally {
      setAddingCandidates(false);
    }
  };

  const handleDeleteInvite = async () => {
    if (!deleteModal) return;

    try {
      await api.delete(`/sessions/${id}/invites/${deleteModal.id}`);
      setDeleteModal(null);
      fetchSession();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to remove candidate');
    }
  };

  const handleGrantRetake = async (inviteId, candidateName) => {
    if (!window.confirm(`Grant retake to ${candidateName}? This will create a new access code for them.`)) {
      return;
    }

    try {
      const response = await api.post(`/sessions/${id}/invites/${inviteId}/retake`);
      alert(`Retake granted! New access code: ${response.data.invite.access_code}`);
      fetchSession();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to grant retake');
    }
  };

  const handleCloseAttempt = async (inviteId, candidateName) => {
    if (!window.confirm(
      `Close ${candidateName}'s exam now? It will be graded on the answers saved so far, ` +
      'and the candidate will not be able to continue. You can then grant a retake.'
    )) {
      return;
    }

    try {
      await api.post(`/sessions/${id}/invites/${inviteId}/close-attempt`);
      fetchSession();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to close the exam');
    }
  };

  const handleSendEmail = async (inviteId, candidateEmail) => {
    setSendingEmail(prev => ({ ...prev, [inviteId]: true }));
    try {
      await api.post(`/sessions/${id}/invites/${inviteId}/send-email`, {
        examBaseUrl: window.location.origin
      });
      alert(`Email sent successfully to ${candidateEmail}`);
      fetchSession(); // Refresh to show email_sent_at
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingEmail(prev => ({ ...prev, [inviteId]: false }));
    }
  };

  const handleSendBulkEmails = async () => {
    if (selectedForEmail.length === 0) {
      alert('Please select candidates to send emails to');
      return;
    }

    if (!window.confirm(`Send emails to ${selectedForEmail.length} candidate(s)?`)) {
      return;
    }

    setSendingBulkEmail(true);
    try {
      const response = await api.post(`/sessions/${id}/send-emails`, {
        inviteIds: selectedForEmail,
        examBaseUrl: window.location.origin
      });

      let message = `Emails sent: ${response.data.sent}`;
      if (response.data.failed > 0) {
        message += `\nFailed: ${response.data.failed}`;
        if (response.data.results?.failed?.length > 0) {
          message += '\n\nFailed emails:\n' + response.data.results.failed.map(f => `${f.email}: ${f.error}`).join('\n');
        }
      }
      alert(message);
      setSelectedForEmail([]);
      fetchSession();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send emails');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  const toggleSelectForEmail = (inviteId) => {
    setSelectedForEmail(prev =>
      prev.includes(inviteId)
        ? prev.filter(id => id !== inviteId)
        : [...prev, inviteId]
    );
  };

  const toggleSelectAllForEmail = () => {
    const pendingInvites = session?.invites?.filter(i => i.status === 'pending') || [];
    if (selectedForEmail.length === pendingInvites.length) {
      setSelectedForEmail([]);
    } else {
      setSelectedForEmail(pendingInvites.map(i => i.id));
    }
  };

  const openValidityModal = (invite) => {
    setSelectedInvite({
      ...invite,
      valid_from: invite.valid_from ? toDateTimeLocal(invite.valid_from) : '',
      valid_until: invite.valid_until ? toDateTimeLocal(invite.valid_until) : ''
    });
    setShowValidityModal(true);
  };

  const handleUpdateValidity = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await api.put(`/sessions/${id}/invites/${selectedInvite.id}`, {
        valid_from: selectedInvite.valid_from ? toISOWithTimezone(selectedInvite.valid_from) : null,
        valid_until: selectedInvite.valid_until ? toISOWithTimezone(selectedInvite.valid_until) : null
      });
      setShowValidityModal(false);
      setSelectedInvite(null);
      fetchSession();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update validity');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const copyAllCodes = () => {
    if (!session?.invites?.length) return;

    const text = session.invites.map(inv =>
      `${inv.candidate_name}\t${inv.candidate_email}\t${inv.access_code}\t${window.location.origin}/exam?code=${inv.access_code}`
    ).join('\n');

    navigator.clipboard.writeText(text);
    alert('All access codes copied to clipboard!');
  };

  // kind: 'links' (access codes) or 'results' (scores, answers and the extra
  // details asked before the exam).
  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to export');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/export/session/${id}/${kind}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const prefix = kind === 'links' ? 'Exam_Links' : 'Session_Results';
      a.download = `${prefix}_${session.session_name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error(`Export ${kind} error:`, error);
      alert(`Failed to export ${kind}`);
    } finally {
      setExporting(null);
    }
  };

  const handleImportCandidates = async (e) => {
    e.preventDefault();
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError('');
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await api.post(`/sessions/${id}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      fetchSession();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to import candidates');
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    setError('');
  };

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to download template');
      return;
    }

    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/sessions/template/download`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => {
      if (!response.ok) throw new Error('Download failed');
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'candidate_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
    .catch(error => {
      console.error('Template download error:', error);
      alert('Failed to download template');
    });
  };

  const getStatusBadge = (invite) => {
    switch (invite.status) {
      case 'completed':
        return <span className="badge badge-success">Completed</span>;
      case 'started':
        return <span className="badge badge-warning">In Progress</span>;
      case 'expired':
        return <span className="badge badge-danger">Expired</span>;
      default:
        return <span className="badge badge-secondary">Pending</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const formatCreatedDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getDateGroup = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Filter and group invites
  const getFilteredAndGroupedInvites = () => {
    if (!session?.invites) return {};

    // Apply filters
    let filtered = session.invites.filter(invite => {
      const matchesSearch = !searchFilter ||
        invite.candidate_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        invite.candidate_email.toLowerCase().includes(searchFilter.toLowerCase()) ||
        invite.access_code.toLowerCase().includes(searchFilter.toLowerCase());

      const matchesStatus = !statusFilter || invite.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Group by date
    const groups = {};
    filtered.forEach(invite => {
      const groupName = getDateGroup(invite.created_at);
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(invite);
    });

    return groups;
  };

  const groupedInvites = session ? getFilteredAndGroupedInvites() : {};
  const filteredCount = Object.values(groupedInvites).flat().length;

  const getValidityInfo = (invite) => {
    const validFrom = invite.valid_from || session?.scheduled_start;
    const validUntil = invite.valid_until || session?.scheduled_end;
    const hasCustomValidity = invite.valid_from || invite.valid_until;

    return {
      from: validFrom,
      until: validUntil,
      isCustom: hasCustomValidity
    };
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!session) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>Session not found</h3>
          <Link to="/sessions" className="btn btn-primary mt-3">Back to Sessions</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-between flex-center mb-4">
        <div>
          <Link to="/sessions" className="text-muted mb-2" style={{ display: 'block', textDecoration: 'none' }}>
            &larr; Back to Sessions
          </Link>
          <h1>{session.session_name}</h1>
          <p className="text-muted">Quiz: {session.quiz_title}</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => setShowCopyModal(true)}
            title="Copy candidates from another exam session or from training attendance">
            Copy Candidates
          </button>
          <button className="btn btn-secondary" onClick={() => setShowImportModal(true)}>
            Import CSV/Excel
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Candidates
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      {/* Session Info */}
      <div className="card mb-4">
        <h3 className="mb-3">Session Details</h3>
        <div className="grid grid-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div>
            <p className="text-muted text-sm">Default Start Time</p>
            <p><strong>{formatDate(session.scheduled_start)}</strong></p>
          </div>
          <div>
            <p className="text-muted text-sm">Default End Time</p>
            <p><strong>{formatDate(session.scheduled_end)}</strong></p>
          </div>
          <div>
            <p className="text-muted text-sm">Duration</p>
            <p><strong>{session.time_limit_minutes} minutes</strong></p>
          </div>
        </div>
      </div>

      <IntroFieldsPanel
        sessionId={session.id}
        fields={session.intro_fields || []}
        sessions={allSessions}
        onChanged={fetchSession}
      />

      {/* Candidates List */}
      <div className="card">
        <div className="flex flex-between flex-center mb-4">
          <h3>Candidates ({session.invites?.length || 0})</h3>
          <div className="flex gap-2">
            {session.invites?.length > 0 && (
              <button
                className="btn btn-success btn-sm"
                onClick={() => handleExport('results')}
                disabled={!!exporting}
                title="Scores, answers and the extra details candidates gave before the exam"
              >
                {exporting === 'results' ? 'Exporting...' : 'Export Results'}
              </button>
            )}
            {session.invites?.length > 0 && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleExport('links')}
                disabled={!!exporting}
              >
                {exporting === 'links' ? 'Exporting...' : 'Export Links'}
              </button>
            )}
            {session.invites?.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={copyAllCodes}>
                Copy All Codes
              </button>
            )}
            {emailConfigured && selectedForEmail.length > 0 && (
              <button
                className="btn btn-success btn-sm"
                onClick={handleSendBulkEmails}
                disabled={sendingBulkEmail}
              >
                {sendingBulkEmail ? 'Sending...' : `Send Emails (${selectedForEmail.length})`}
              </button>
            )}
          </div>
        </div>

        {/* Email Configuration Notice */}
        {!emailConfigured && session.invites?.length > 0 && (
          <div style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#92400e'
          }}>
            Email sending is not configured. Contact your administrator to set up SMTP settings for email invitations.
          </div>
        )}

        {/* Filters */}
        {session.invites?.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <input
              type="text"
              placeholder="Search by name, email, or code..."
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '250px',
                flex: '1',
                maxWidth: '350px'
              }}
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '140px'
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="started">In Progress</option>
              <option value="completed">Completed</option>
              <option value="expired">Expired</option>
            </select>
            {(searchFilter || statusFilter) && (
              <button
                onClick={() => { setSearchFilter(''); setStatusFilter(''); }}
                style={{
                  padding: '8px 12px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Clear
              </button>
            )}
            {(searchFilter || statusFilter) && (
              <span className="text-muted text-sm">
                Showing {filteredCount} of {session.invites.length}
              </span>
            )}
          </div>
        )}

        {!session.invites?.length ? (
          <div className="empty-state">
            <h4>No candidates added yet</h4>
            <p>Add candidates to generate individual access codes</p>
            <button className="btn btn-primary mt-3" onClick={() => setShowAddModal(true)}>
              Add Candidates
            </button>
          </div>
        ) : filteredCount === 0 ? (
          <div className="empty-state">
            <h4>No matching candidates</h4>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div>
            {Object.entries(groupedInvites).map(([dateGroup, invites]) => (
              <div key={dateGroup} style={{ marginBottom: '24px' }}>
                {/* Date Group Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  <span style={{
                    background: dateGroup === 'Today' ? '#dcfce7' : dateGroup === 'Yesterday' ? '#fef3c7' : '#f3f4f6',
                    color: dateGroup === 'Today' ? '#166534' : dateGroup === 'Yesterday' ? '#92400e' : '#374151',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    {dateGroup}
                  </span>
                  <span className="text-muted text-sm">
                    {invites.length} candidate{invites.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <table className="table">
                  <thead>
                    <tr>
                      {emailConfigured && (
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedForEmail.length > 0 && selectedForEmail.length === (session?.invites?.filter(i => i.status === 'pending')?.length || 0)}
                            onChange={toggleSelectAllForEmail}
                            title="Select all pending candidates"
                            style={{ width: '16px', height: '16px' }}
                          />
                        </th>
                      )}
                      <th>Name</th>
                      <th>Access Code</th>
                      <th>Created</th>
                      <th>Validity</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map(invite => {
                      const validity = getValidityInfo(invite);
                      return (
                        <tr key={invite.id}>
                          {emailConfigured && (
                            <td>
                              {invite.status === 'pending' && (
                                <input
                                  type="checkbox"
                                  checked={selectedForEmail.includes(invite.id)}
                                  onChange={() => toggleSelectForEmail(invite.id)}
                                  style={{ width: '16px', height: '16px' }}
                                />
                              )}
                            </td>
                          )}
                          <td>
                            <strong>{invite.candidate_name}</strong>
                            <div className="text-muted text-sm">{invite.candidate_email}</div>
                            {invite.email_sent_at && (
                              <div style={{ fontSize: '11px', color: '#10b981', marginTop: '2px' }}>
                                Email sent {new Date(invite.email_sent_at).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-2 flex-center">
                              <code style={{
                                background: '#f3f4f6',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                letterSpacing: '1px',
                                cursor: 'pointer'
                              }}
                                onClick={() => {
                                  copyToClipboard(invite.access_code);
                                  alert('Access code copied!');
                                }}
                                title="Click to copy code"
                              >
                                {invite.access_code}
                              </code>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => {
                                  copyToClipboard(`${window.location.origin}/exam?code=${invite.access_code}`);
                                  alert('Exam link copied!');
                                }}
                                title="Copy exam link with code"
                              >
                                Link
                              </button>
                            </div>
                          </td>
                          <td style={{ fontSize: '12px', color: '#6b7280' }}>
                            {formatCreatedDate(invite.created_at)}
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            <div style={{ color: validity.isCustom ? '#f97316' : '#6b7280' }}>
                              {validity.isCustom && <span style={{ fontWeight: 'bold' }}>Custom: </span>}
                              <div>{formatDate(validity.from)}</div>
                              <div>to {formatDate(validity.until)}</div>
                            </div>
                          </td>
                          <td>
                            {getStatusBadge(invite)}
                            {END_REASON_LABELS[invite.end_reason] && (
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                {END_REASON_LABELS[invite.end_reason]}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-2">
                              {emailConfigured && invite.status === 'pending' && (
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => handleSendEmail(invite.id, invite.candidate_email)}
                                  disabled={sendingEmail[invite.id]}
                                  title={invite.email_sent_at ? 'Resend email' : 'Send email invitation'}
                                  style={{ minWidth: '60px' }}
                                >
                                  {sendingEmail[invite.id] ? '...' : (invite.email_sent_at ? 'Resend' : 'Email')}
                                </button>
                              )}
                              {invite.status === 'pending' && (
                                <button
                                  className="btn btn-outline btn-sm"
                                  onClick={() => openValidityModal(invite)}
                                  title="Set custom validity"
                                >
                                  Schedule
                                </button>
                              )}
                              {invite.attempt_status === 'in_progress' && (
                                <button
                                  className="btn btn-outline btn-sm"
                                  onClick={() => handleCloseAttempt(invite.id, invite.candidate_name)}
                                  title="Close this exam now and grade the answers saved so far"
                                >
                                  Close now
                                </button>
                              )}
                              {invite.status === 'completed' && (
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => handleGrantRetake(invite.id, invite.candidate_name)}
                                  title="Grant Retake"
                                >
                                  Retake
                                </button>
                              )}
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => setDeleteModal(invite)}
                                title="Delete candidate"
                              >
                                X
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Exam Link Info */}
      <div className="card mt-4">
        <h3 className="mb-2">Exam Access</h3>
        <p className="text-muted mb-3">
          Each candidate uses their unique access code at this URL:
        </p>
        <code style={{
          display: 'block',
          background: '#f3f4f6',
          padding: '12px',
          borderRadius: '8px',
          wordBreak: 'break-all'
        }}>
          {window.location.origin}/exam
        </code>
      </div>

      {/* Add Candidates Modal */}
      {showAddModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '650px', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: '8px' }}>Add Candidates</h2>
            <p className="text-muted" style={{ marginBottom: '20px' }}>
              Enter candidate details below. Each will receive a unique access code.
            </p>

            <form onSubmit={handleAddCandidates}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useCustomValidity}
                    onChange={e => setUseCustomValidity(e.target.checked)}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Set custom validity dates for each candidate</span>
                </label>
              </div>

              {candidates.map((candidate, index) => (
                <div key={index} style={{
                  padding: '16px',
                  marginBottom: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: '500', color: '#475569' }}>Candidate {index + 1}</span>
                    {candidates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCandidateRow(index)}
                        style={{
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 12px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={candidate.name}
                      onChange={e => updateCandidate(index, 'name', e.target.value)}
                      placeholder="Enter full name"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        fontSize: '15px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: useCustomValidity ? '12px' : '0' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={candidate.email}
                      onChange={e => updateCandidate(index, 'email', e.target.value)}
                      placeholder="Enter email address"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        fontSize: '15px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {useCustomValidity && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                          Valid From
                        </label>
                        <input
                          type="datetime-local"
                          value={candidate.valid_from}
                          onChange={e => updateCandidate(index, 'valid_from', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                          Valid Until
                        </label>
                        <input
                          type="datetime-local"
                          value={candidate.valid_until}
                          onChange={e => updateCandidate(index, 'valid_until', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addCandidateRow}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '20px',
                  background: '#f1f5f9',
                  border: '2px dashed #cbd5e1',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  color: '#475569',
                  fontWeight: '500'
                }}
              >
                + Add Another Candidate
              </button>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setCandidates([{ name: '', email: '', valid_from: '', valid_until: '' }]);
                    setUseCustomValidity(false);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#475569'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingCandidates}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#f97316',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: addingCandidates ? 'not-allowed' : 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: 'white',
                    opacity: addingCandidates ? 0.7 : 1
                  }}
                >
                  {addingCandidates ? 'Adding...' : 'Generate Access Codes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Validity Modal */}
      {showValidityModal && selectedInvite && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '450px', margin: '20px' }}>
            <h2 className="mb-4">Set Access Code Validity</h2>
            <p className="text-muted mb-4">
              Set custom start/end times for <strong>{selectedInvite.candidate_name}</strong>.
              Leave empty to use session defaults.
            </p>

            <form onSubmit={handleUpdateValidity}>
              <div className="form-group">
                <label>Valid From</label>
                <input
                  type="datetime-local"
                  value={selectedInvite.valid_from}
                  onChange={e => setSelectedInvite({ ...selectedInvite, valid_from: e.target.value })}
                />
                <small className="text-muted">
                  Session default: {formatDate(session.scheduled_start)}
                </small>
              </div>

              <div className="form-group">
                <label>Valid Until</label>
                <input
                  type="datetime-local"
                  value={selectedInvite.valid_until}
                  onChange={e => setSelectedInvite({ ...selectedInvite, valid_until: e.target.value })}
                />
                <small className="text-muted">
                  Session default: {formatDate(session.scheduled_end)}
                </small>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    setShowValidityModal(false);
                    setSelectedInvite(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setSelectedInvite({ ...selectedInvite, valid_from: '', valid_until: '' });
                  }}
                >
                  Clear
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCopyModal && (
        <CopyCandidatesModal
          sessionId={id}
          sessions={allSessions}
          existingEmails={new Set((session.invites || []).map(i => String(i.candidate_email || '').trim().toLowerCase()))}
          onClose={() => setShowCopyModal(false)}
          onCopied={() => { setShowCopyModal(false); fetchSession(); }}
        />
      )}

      {/* Import Candidates Modal */}
      {showImportModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', margin: '20px' }}>
            <h2 className="mb-2">Import Candidates</h2>
            <p className="text-muted mb-4">
              Upload a CSV or Excel file with candidate names and emails.
            </p>

            {!importResult ? (
              <form onSubmit={handleImportCandidates}>
                <div style={{
                  border: '2px dashed #cbd5e1',
                  borderRadius: '8px',
                  padding: '30px',
                  textAlign: 'center',
                  marginBottom: '20px',
                  background: importFile ? '#f0fdf4' : '#f8fafc'
                }}>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={e => setImportFile(e.target.files[0])}
                    style={{ display: 'none' }}
                    id="import-file-input"
                  />
                  <label htmlFor="import-file-input" style={{ cursor: 'pointer' }}>
                    {importFile ? (
                      <div>
                        <div style={{ fontSize: '40px', marginBottom: '10px' }}>📄</div>
                        <div style={{ fontWeight: '500', color: '#16a34a' }}>{importFile.name}</div>
                        <div className="text-muted text-sm mt-2">Click to change file</div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: '40px', marginBottom: '10px' }}>📁</div>
                        <div style={{ fontWeight: '500' }}>Click to select file</div>
                        <div className="text-muted text-sm mt-2">Supports CSV, XLSX, XLS</div>
                      </div>
                    )}
                  </label>
                </div>

                <div style={{
                  background: '#fef3c7',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '13px'
                }}>
                  <strong>File format:</strong> Your file should have columns for <code>name</code> and <code>email</code>.
                  Headers like "Name", "Email", "Candidate Name", "Candidate Email" are all supported.
                  <div style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      style={{
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Download Sample Template
                    </button>
                  </div>
                </div>

                {error && <div className="alert alert-error mb-4">{error}</div>}

                <div className="flex gap-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                    onClick={closeImportModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    disabled={!importFile || importing}
                  >
                    {importing ? 'Importing...' : 'Import Candidates'}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '40px', marginBottom: '10px' }}>✅</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#16a34a' }}>
                    Import Successful!
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <strong>{importResult.imported}</strong> candidate{importResult.imported !== 1 ? 's' : ''} imported
                  </div>
                  {importResult.skipped > 0 && (
                    <div className="text-muted text-sm mt-2">
                      {importResult.skipped} skipped (duplicate emails)
                    </div>
                  )}
                </div>

                {importResult.skipped > 0 && (
                  <div style={{
                    background: '#fef3c7',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    fontSize: '13px'
                  }}>
                    <strong>Skipped emails:</strong>
                    <div className="text-muted mt-1">
                      {importResult.skippedEmails.join(', ')}
                    </div>
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={closeImportModal}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Candidate Modal */}
      {deleteModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setDeleteModal(null))}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Candidate</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteModal.candidate_name}</strong>?</p>
              {deleteModal.status === 'completed' && (
                <p className="text-muted mt-2" style={{ color: '#dc2626' }}>
                  Warning: This candidate has already completed the exam. Deleting will remove their attempt and results.
                </p>
              )}
              {deleteModal.status === 'started' && (
                <p className="text-muted mt-2" style={{ color: '#f59e0b' }}>
                  Warning: This candidate is currently taking the exam. Deleting will terminate their session.
                </p>
              )}
              <p className="text-muted mt-2">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteInvite}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionDetail;
